import { Button, Drawer, Dropdown, MenuProps, Popover } from 'antd';
import {
  ArrowDown2,
  HambergerMenu,
  LogoutCurve,
  Moon,
  NotificationBing,
  ProfileCircle,
  Sun1,
  UserSquare,
} from 'iconsax-react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { Logout, NotificationList } from '@src/pages/Dashboard';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PROFILE_FULL_URL } from '@src/constants/URLS.const';
import { selectLayout, setTheme, THEME } from '@src/store/layout/layoutSlice';
import { CountryRegionSearch } from '@src/components/CountryRegionSearch';
import { RightSidebar, Sidebar } from '../Sidebar';
import { SidebarLoading, WhiteLabelLogo } from '@src/components';
import { selectCurrentUser } from '@src/store/user/userSlice';

export function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const layout = useSelector(selectLayout);
  const user = useSelector(selectCurrentUser);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [confirmLogOutOpen, setConfirmLogOutOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    const selectedTheme =
      layout.theme === THEME.LIGHT ? THEME.DARK : THEME.LIGHT;
    dispatch(setTheme(selectedTheme));
    document.documentElement.classList.toggle(
      'dark',
      selectedTheme === THEME.DARK
    );
  };

  const userMenuItems: MenuProps['items'] = useMemo(
    () =>
      [
        {
          key: 'email',
          label: (
            <div className='text-gray-400 px-3 py-2 italic'>{user?.email}</div>
          ),
          disabled: true,
          className:
            '!bg-transparent hover:!bg-transparent dark:hover:!bg-wl-surface !cursor-default',
        },
        {
          type: 'divider',
          className: 'dark:!bg-gray-600 !bg-gray-300',
        },
        {
          key: 'profile',
          label: (
            <div className='flex flex-row items-end gap-2 text-wl-on-background px-3 py-2'>
              <ProfileCircle size='24' variant='Bulk' />
              <span>{t('navbar.profile')}</span>
            </div>
          ),
          onClick: () => navigate(ROUTE_PROFILE_FULL_URL),
        },
        {
          type: 'divider',
          className: 'dark:!bg-gray-600 !bg-gray-300',
        },
        {
          key: 'logout',
          label: (
            <div className='flex flex-row gap-2 justify-start px-3 py-2 w-full text-red-500'>
              <LogoutCurve size='24' variant='Bulk' />
              <span>{t('buttons.signOut')}</span>
            </div>
          ),
          onClick: () => setConfirmLogOutOpen(true),
        },
      ].filter(Boolean) as MenuProps['items'],
    [isDesktop, navigate, toggleTheme, t]
  );

  const handleCloseLogOutConfirm = () => {
    setTimeout(() => {
      setConfirmLogOutOpen(false);
    }, 300);
  };

  return (
    <div className='w-full flex items-center justify-between py-2'>
      {!isDesktop && (
        <div className='w-full'>
          <div className='w-full flex items-center justify-between'>
            <Button
              type='text'
              className='md:hidden !p-0'
              onClick={() => setLeftSidebarOpen(true)}
            >
              <HambergerMenu
                variant='Bulk'
                size={32}
                className='text-wl-on-background'
              />
            </Button>

            <div className='flex items-center space-x-1 text-lg font-semibold'>
              <WhiteLabelLogo />
            </div>

            <UserSquare
              size={32}
              variant='Bulk'
              className='text-wl-on-background'
              onClick={() => setRightSidebarOpen(true)}
            />
          </div>
        </div>
      )}
      {isDesktop && (
        <div className='flex justify-center w-full items-center'>
          <CountryRegionSearch />
          <div className='flex items-center ml-auto'>
            <div className='hidden md:flex items-center space-x-1'>
              {layout.theme === THEME.DARK ? (
                <Sun1
                  className='cursor-pointer text-wl-on-background'
                  size={32}
                  variant='Bulk'
                  opacity={0.7}
                  onClick={toggleTheme}
                />
              ) : (
                <Moon
                  className='cursor-pointer text-wl-on-background'
                  size={32}
                  variant='Bold'
                  opacity={0.7}
                  onClick={toggleTheme}
                />
              )}
            </div>
            <div className='border-l border-gray-300 h-6 mx-2'></div>
            <div className='flex space-x-2'>
              <Dropdown
                menu={{
                  items: userMenuItems,
                  className:
                    '!bg-wl-background [&_.ant-dropdown-menu-item-active]:hover:!bg-wl-surface',
                }}
                placement='bottomRight'
              >
                <div className='flex items-center pl-1 cursor-pointer'>
                  <UserSquare
                    size={26}
                    variant='Bulk'
                    className='text-wl-on-background'
                  />
                  <ArrowDown2
                    size='16'
                    variant='Bold'
                    className='text-wl-on-background'
                  />
                </div>
              </Dropdown>

              <div className='flex items-center'>
                <Popover
                  content={<NotificationList showHeader />}
                  trigger='click'
                  placement='bottomLeft'
                  arrow={false}
                  color='!text-wl-background'
                  classNames={{
                    body: '!bg-wl-surface !text-wl-on-surface !rounded-2xl',
                  }}
                >
                  <NotificationBing
                    className='cursor-pointer text-wl-on-background'
                    size={32}
                    variant='Bulk'
                  />
                </Popover>
              </div>
            </div>
            <div className='flex md:hidden'>
              <UserSquare
                size={26}
                variant='Bulk'
                className='text-wl-on-background'
                onClick={() => setRightSidebarOpen(true)}
              />
            </div>
          </div>
        </div>
      )}
      <Drawer
        push={false}
        width='100vw'
        placement='left'
        onClose={() => setLeftSidebarOpen(false)}
        closable={false}
        open={leftSidebarOpen}
        rootClassName='md:hidden'
        styles={{
          body: {
            backgroundColor: '#f3f4f6',
            padding: 0,
          },
          header: {
            backgroundColor: '#f3f4f6',
            margin: 0,
          },
          content: {
            width: 'auto',
          },
        }}
      >
        <Suspense fallback={<SidebarLoading />}>
          <Sidebar onClose={() => setLeftSidebarOpen(false)} />
        </Suspense>
      </Drawer>

      <Drawer
        push={false}
        width='100vw'
        placement='right'
        onClose={() => setRightSidebarOpen(false)}
        closable={false}
        open={rightSidebarOpen}
        rootClassName='md:hidden'
        styles={{
          body: {
            backgroundColor: '#f3f4f6',
            padding: 0,
          },
          header: {
            backgroundColor: '#f3f4f6',
            margin: 0,
          },
          content: {
            width: 'auto',
          },
        }}
      >
        <RightSidebar
          currentUserEmail={user?.email}
          onClose={() => setRightSidebarOpen(false)}
        />
      </Drawer>

      <ResponsiveModal
        isOpen={confirmLogOutOpen}
        title={t('pages.auth.signOut.title')}
        headerIcon={<LogoutCurve variant='Bulk' />}
        content={<Logout onClose={handleCloseLogOutConfirm} />}
        onClose={handleCloseLogOutConfirm}
      />
    </div>
  );
}
